/* ============================================================
   ROBA LUCA: één gevoel voor alle sliders van de winkel
   Ingeladen met defer vanuit layout/theme.liquid, meteen na
   swiper-bundle.min.js en vóór global.js.

   Uitleg en meetwaarden: docs/shop/theme-custom-code.md §49, §84
   en §116.
   ============================================================ */
(() => {
  'use strict';

  /* Elke slider van het thema is een Swiper, en elk themascript
     geeft zijn eigen instellingen mee. Die zitten in JavaScript; CSS
     kan er niet aan. Hier staat dus de enige plek waar ze gezet
     worden: window.Swiper wordt één keer ingepakt, zodat élke Swiper
     die daarna gemaakt wordt onze waarden krijgt. Ook de sliders die
     Zenith na het laden of bij een variantwissel opnieuw opbouwt, en
     die in de snelkijker.

     Waarom een eigen bestand op deze plek: global.js haalt de
     aanbevelingen en Wear it with op met een fetch zodra het laadt,
     en bouwt hun slider zodra het antwoord er is. Stond deze
     inpakking later (tot 05/10 in rl-custom.js), dan was dat antwoord
     er vaak eerder en kregen die twee de gewone Swiper. Snelheid en
     breedte werden toen achteraf rechtgezet; native scroll kan niet
     achteraf, dat moet bij het aanmaken. Uitgestelde scripts lopen in
     de volgorde van de pagina, dus vanaf hier gaat elke Swiper langs
     deze constructor. */
  if (!window.Swiper || window.Swiper.rlIngepakt) return;

  /* Snelheid 200 ms (thema: 800), de helft van de sleep die Zenith
     vraagt om naar de volgende te gaan (0,25 tegen 0,5), en minder
     doorveren aan het einde (0,65 tegen 0,85). Sinds §86 scrollt elke
     slider die je kan vegen native (hieronder); daar zorgt een
     snelheid boven 0 alleen nog dat de pijltjes vloeiend schuiven, en
     het tempo kiest de browser. De waarden gelden volledig voor een
     slider die niet native scrollt: een slider met een fade-effect
     of waarop vegen uitstaat (nu nergens op de site). */
  const RL_SLIDER = {
    speed: 200,
    longSwipesRatio: 0.25,
    resistanceRatio: 0.65,
  };

  /* Hoeveel dia's per scherm, in de basisstand: dat is op telefoon.
     Waar het thema vanaf een bepaalde breedte meer kolommen toont,
     blijven die staan. De eerste regel die past, wint.
     - Alle productrijen (Zeniths .popular-products__layout: New in op
       de homepage, de aanbevelingen, recent bekeken en de
       winkelmand): een kaart is 65 % van de breedte, dus 1 / 0,65 =
       1,54 per scherm. Je ziet een derde van de volgende kaart. New
       in sinds 05/10/2026 (§89), de andere rijen sinds §116: ze
       scrollen nu vrij (RL_VRIJ hieronder), en dat werkt alleen goed
       als er meer dan één kaart in beeld staat.
     - Wear it with en de fotogalerij op de productpagina: 1,2 per
       scherm, dus één kaart of foto met een randje van de volgende
       (§49).
     De zoom-popup staat bewust niet in de lijst: daar hoort één foto
     over het volle scherm.
     Dezelfde breedtes staan ook in snippets/product-card.liquid
     (rl_mobiel_vw, §92): daar bepalen ze hoe breed de foto is die een
     telefoon laadt. Verander je hier iets, verander het daar ook. */
  const RL_PEEK = [
    ['.popular-products__layout', 1 / 0.65], // New in, aanbevelingen, recent bekeken, winkelmand
    ['.js-media-list', 1.2],                 // fotogalerij op de productpagina
    ['.js-complementary-slider', 1.2],       // Wear it with
  ];

  /* De productkaarten raken elkaar: hun randen vormen de lijnen
     tussen de kaarten (thema: 1 px ruimte). Wear it with houdt zijn
     8 px, want daar heeft elke kaart een eigen kader. De galerij
     staat bij het thema al op 0. */
  const RL_ZONDER_RUIMTE = '.popular-products__layout';

  /* Vrij scrollen in de productrijen (§116, Lucas 18/10/2026, naar
     het voorbeeld van Nude Project): de rij stopt waar de vaart van
     de veeg ophoudt, zonder vast te klikken op een kaart. In cssMode
     doet Swipers freeMode alleen dat: Swipers eigen CSS zet dan
     scroll-snap-type op none (.swiper-free-mode in
     swiper-bundle.min.css). Het momentum is dat van de browser zelf.
     De pijltjes schuiven nog altijd één kaart en zetten die tegen de
     rand.
     Niet vrij: Wear it with, de fotogalerij en de zoom-popup. Daar
     staat één groot beeld per scherm, en vrij scrollen zou de rij
     tussen twee beelden laten stoppen. */
  const RL_VRIJ = '.popular-products__layout';

  /* Native scroll in elke slider die je kan vegen, op elk toestel
     (§84, §86). Swiper laat de rij dan door de browser zelf scrollen
     (cssMode: overflow en scroll-snap) in plaats van ze met een eigen
     overgang te verschuiven.
     - Op een iPhone verdwijnt zo de hapering bij het loslaten: daar
       gaan 1 à 2 beeldjes verloren voor een overgang op gang komt
       (§82).
     - Met een trackpad scrolt de rij zoals alles op een Mac. Swiper
       las het trackpad zelf en zag elk schokje in de uitloop als een
       nieuwe veeg; de rij schoof dan drie kaarten of meer ver (§86).
     - Klikken en slepen met de muis kan niet meer met cssMode. De
       pijltjes, het trackpad en shift + scrollwiel blijven werken.
     Hoe ver één veeg gaat, staat bij perVeeg hieronder en in
     rl-custom.css §30. */
  const ThemaSwiper = window.Swiper;

  class RlSwiper extends ThemaSwiper {
    constructor(...args) {
      // Swiper aanvaardt (element, instellingen) of alleen (instellingen).
      const alleenInstellingen = args.length === 1
        && Object.prototype.toString.call(args[0]) === '[object Object]';
      const el = alleenInstellingen ? args[0].el : args[0];
      const params = { ...(alleenInstellingen ? args[0] : args[1]) };
      const node = typeof el === 'string' ? document.querySelector(el) : el;

      /* Een lopende band (autoplay, of een snelheid boven 3 s, zoals de
         Instagram-rij op de homepage met 9 s) is geen slider om te
         vegen: daar is de snelheid het tempo van de band. */
      const lopendeBand = Boolean(params.autoplay) || (params.speed || 0) > 3000;
      if (!lopendeBand && params.speed !== 0) Object.assign(params, RL_SLIDER);

      /* Zenith forceert op telefoon twee kolommen met een !important op
         .popular-products__list--mobile-2, en die verslaat de breedte die
         Swiper op de kaarten zet. rl-custom.js §1 haalt die klasse weg bij
         DOMContentLoaded en load, maar de aanbevelingen komen later binnen
         (global.js haalt ze met een fetch op). Kwam dat antwoord na load,
         dan bleven ze op twee per scherm staan. Gevonden bij het testen van
         §116. Hier, bij het aanmaken van de slider, is dat altijd op tijd. */
      if (node && node.matches && node.matches('.popular-products__layout')) {
        node.querySelectorAll('.popular-products__list').forEach((lijst) => {
          [...lijst.classList]
            .filter((klasse) => /^popular-products__list--mobile-\d$/.test(klasse))
            .forEach((klasse) => lijst.classList.remove(klasse));
        });
      }

      const peek = node && node.matches && RL_PEEK.find(([selector]) => node.matches(selector));
      if (peek) params.slidesPerView = peek[1];
      if (node && node.matches && node.matches(RL_ZONDER_RUIMTE)) params.spaceBetween = 0;

      const veegbaar = !lopendeBand
        && params.allowTouchMove !== false
        && (!params.effect || params.effect === 'slide');
      const vrij = Boolean(veegbaar && node && node.matches && node.matches(RL_VRIJ));
      if (veegbaar) {
        params.cssMode = true;
        /* Niet rondlopen (na de laatste foto weer de eerste). Een rij
           die de browser zelf scrollt, heeft een begin en een einde,
           zoals een carrousel op Instagram. Swiper kan het wel, maar
           schuift dan bij elke aanraking de dia's in de pagina om en
           zet de scrollpositie terug: net het soort sprong dat native
           scroll hier weghaalt. Gaat over de fotogalerij en de
           zoom-popup; de andere sliders liepen al niet rond. */
        params.loop = false;
        if (vrij) params.freeMode = { enabled: true, sticky: false };
      }

      if (alleenInstellingen) super(params);
      else super(args[0], params);

      /* Eén kaart per veeg waar minder dan twee kaarten in beeld
         staan: op telefoon in de fotogalerij, in Wear it with en in
         de zoom-popup. Zo gaat ook de iPhone zelf om met grote
         beelden (Foto's, Instagram). De productrijen scrollen vrij
         (RL_VRIJ, §116) en krijgen deze klasse nooit. Staan er twee
         of meer beelden naast elkaar (tablet, computer), dan loopt
         de rij uit tot ze stopt, en stopt ze netjes op een beeld. Het aantal per scherm
         wisselt met de breedte (breakpoints van het thema), dus de
         klasse volgt die wissel. */
      if (veegbaar && node && node.classList) {
        const perVeeg = () => {
          const perScherm = this.params.slidesPerView;
          node.classList.toggle('rl-een-per-veeg', !vrij && typeof perScherm === 'number' && perScherm < 2);
        };
        perVeeg();
        this.on('breakpoint', perVeeg);
      }
    }
  }

  RlSwiper.rlIngepakt = true;
  window.Swiper = RlSwiper;

  /* ----------------------------------------------------------
     Slepen met de muis (§87)

     Met cssMode scrollt de browser de rij zelf, en een browser kent
     geen klikken en slepen. Dit stukje voegt dat toe, alleen voor de
     muis: aanraking en trackpad blijven helemaal van de browser.

     - Pas vanaf Swipers eigen drempel (5 px, en meer opzij dan op en
       neer) wordt het een sleep. Daaronder blijft het een gewone
       klik, ook als de muis een paar pixels verschuift.
     - Tijdens het slepen volgt de rij de muis. Het vastklikken op een
       kaart staat dan even uit, anders verspringt de rij onder de
       muis. De muis wordt aan de rij vastgehouden (pointer capture),
       dus kaarten en kleurvierkantjes eronder krijgen geen hover en
       geen klik.
     - Bij het loslaten kiest dit de kaart en schuift er vloeiend
       naartoe (Swipers slideTo, zoals de pijltjes). Waar één kaart de
       breedte vult (rl-een-per-veeg): hooguit één kaart verder dan
       waar je begon, zoals op telefoon. Anders: de kaart het dichtst
       bij waar je loslaat; een snelle veeg gaat hooguit één kaart
       verder in die richting. De vaart van de muis laat de rij nooit
       verder doorlopen: zo verlies je het overzicht niet (Lucas,
       05/10/2026).
     - In een vrije productrij (RL_VRIJ, §116) blijft de rij staan
       waar je loslaat: geen kaart kiezen, en ook daar geen vaart.
     - Na een sleep wordt de klik die de browser nog stuurt
       weggevangen: geen product dat opengaat, geen kleur die wisselt,
       geen zoom-popup.
     - Foto's en links in een slider kan je met de muis niet meer naar
       buiten slepen (bureaublad, nieuw tabblad); dat zou de sleep van
       de rij overnemen.
     ---------------------------------------------------------- */
  const VAART = 0.3;        // px/ms: sneller is een veeg in die richting
  const KORT = 0.2;         // deel van een kaart dat een trage sleep moet halen
  const STIL_MS = 120;      // zolang geen scroll = de rij staat stil

  let sleep = null;

  const dichtstbij = (grid, positie) => grid.reduce(
    (beste, waarde, i) => (Math.abs(waarde - positie) < Math.abs(grid[beste] - positie) ? i : beste),
    0
  );

  // Het vastklikken terugzetten zodra de rij stilstaat.
  const snapTerug = (wrapper) => {
    let wacht;
    const opnieuw = () => {
      clearTimeout(wacht);
      wacht = setTimeout(() => {
        wrapper.removeEventListener('scroll', opnieuw);
        if (sleep && sleep.wrapper === wrapper && sleep.bezig) return;
        wrapper.style.scrollSnapType = '';
      }, STIL_MS);
    };
    wrapper.addEventListener('scroll', opnieuw, { passive: true });
    opnieuw();
  };

  const vangKlik = () => {
    const vang = (gebeurtenis) => {
      gebeurtenis.preventDefault();
      gebeurtenis.stopImmediatePropagation();
    };
    window.addEventListener('click', vang, true);
    // De klik volgt meteen op het loslaten, nog voor deze timer.
    setTimeout(() => window.removeEventListener('click', vang, true), 0);
  };

  document.addEventListener('pointerdown', (gebeurtenis) => {
    if (gebeurtenis.pointerType !== 'mouse' || gebeurtenis.button !== 0) return;
    const doel = gebeurtenis.target;
    const wrapper = doel && doel.closest ? doel.closest('.swiper-css-mode > .swiper-wrapper') : null;
    const swiper = wrapper && wrapper.parentElement.swiper;
    if (!swiper || swiper.destroyed) return;
    if (swiper.zoom && swiper.zoom.scale > 1) return;   // ingezoomd: Swiper verschuift de foto zelf
    sleep = {
      wrapper,
      swiper,
      slider: wrapper.parentElement,
      id: gebeurtenis.pointerId,
      x0: gebeurtenis.clientX,
      y0: gebeurtenis.clientY,
      s0: wrapper.scrollLeft,
      drempel: swiper.params.threshold || 5,
      bezig: false,
      punten: [],
    };
  }, true);

  document.addEventListener('pointermove', (gebeurtenis) => {
    if (!sleep || gebeurtenis.pointerId !== sleep.id) return;
    const dx = gebeurtenis.clientX - sleep.x0;
    const dy = gebeurtenis.clientY - sleep.y0;
    if (!sleep.bezig) {
      if (Math.hypot(dx, dy) < sleep.drempel) return;
      if (Math.abs(dy) > Math.abs(dx)) { sleep = null; return; }   // op en neer: geen sleep
      sleep.bezig = true;
      sleep.wrapper.style.scrollSnapType = 'none';
      sleep.slider.classList.add('rl-sleept');
      sleep.wrapper.setPointerCapture(gebeurtenis.pointerId);
      const selectie = window.getSelection && window.getSelection();
      if (selectie) selectie.removeAllRanges();
    }
    sleep.wrapper.scrollLeft = sleep.s0 - dx;
    sleep.punten.push([gebeurtenis.timeStamp, gebeurtenis.clientX]);
    while (sleep.punten.length > 2 && gebeurtenis.timeStamp - sleep.punten[0][0] > 100) sleep.punten.shift();
  }, true);

  const loslaten = (gebeurtenis) => {
    if (!sleep || gebeurtenis.pointerId !== sleep.id) return;
    const s = sleep;
    sleep = null;
    if (!s.bezig) return;

    // De klasse blijft staan tot de muis echt weer beweegt. Na het
    // loslaten ligt er vaak een kleurvierkantje onder de muis, want de
    // rij schoof met de muis mee; dat mag niet vanzelf de foto wisselen
    // (rl-custom.js §10b kijkt naar deze klasse). Safari stuurt na het
    // loslaten zelf nog een pointermove op dezelfde plek; die telt niet.
    const slider = s.slider;
    const losX = gebeurtenis.clientX;
    const losY = gebeurtenis.clientY;
    const weg = (beweging) => {
      if (Math.hypot(beweging.clientX - losX, beweging.clientY - losY) < 3) return;
      document.removeEventListener('pointermove', weg, true);
      slider.classList.remove('rl-sleept');
    };
    document.addEventListener('pointermove', weg, true);
    if (s.wrapper.hasPointerCapture(gebeurtenis.pointerId)) s.wrapper.releasePointerCapture(gebeurtenis.pointerId);
    if (gebeurtenis.type === 'pointerup') vangKlik();

    // Vrije productrij (§116): blijven staan waar je loslaat.
    if (s.swiper.params.freeMode && s.swiper.params.freeMode.enabled) {
      snapTerug(s.wrapper);
      return;
    }

    // Vaart van de muis in px/ms; stond de muis al even stil, dan geen.
    const eerste = s.punten[0];
    const laatste = s.punten[s.punten.length - 1];
    let vaart = 0;
    if (eerste && laatste && laatste[0] > eerste[0] && gebeurtenis.timeStamp - laatste[0] < 80) {
      vaart = (laatste[1] - eerste[1]) / (laatste[0] - eerste[0]);
    }

    const grid = s.swiper.snapGrid || [];
    if (!grid.length) { snapTerug(s.wrapper); return; }
    const nu = s.wrapper.scrollLeft;
    let doelIndex;
    if (s.slider.classList.contains('rl-een-per-veeg')) {
      const start = dichtstbij(grid, s.s0);
      const kaart = s.swiper.slidesSizesGrid[start] || s.swiper.width;
      const verschoven = nu - s.s0;               // + = vooruit
      let stap = 0;
      if (Math.abs(vaart) > VAART) stap = vaart < 0 ? 1 : -1;
      else if (Math.abs(verschoven) > kaart * KORT) stap = verschoven > 0 ? 1 : -1;
      doelIndex = start + stap;
    } else {
      doelIndex = dichtstbij(grid, nu);
      // Een snelle veeg: nog één kaart in die richting, tenzij de
      // dichtste kaart al in die richting ligt.
      if (Math.abs(vaart) > VAART) {
        const richting = vaart < 0 ? 1 : -1;      // muis naar links = vooruit
        const plek = grid[doelIndex];
        if ((richting > 0 && plek <= nu) || (richting < 0 && plek >= nu)) doelIndex += richting;
      }
    }
    doelIndex = Math.max(0, Math.min(grid.length - 1, doelIndex));
    s.swiper.slideTo(doelIndex);
    snapTerug(s.wrapper);
  };

  document.addEventListener('pointerup', loslaten, true);
  document.addEventListener('pointercancel', loslaten, true);

  // Geen foto of link uit een slider slepen: dat zou de sleep van de
  // rij overnemen (de browser stuurt dan pointercancel).
  document.addEventListener('dragstart', (gebeurtenis) => {
    const doel = gebeurtenis.target;
    const el = doel && doel.nodeType === 1 ? doel : doel && doel.parentElement;
    if (el && el.closest && el.closest('.swiper-css-mode')) gebeurtenis.preventDefault();
  }, true);
})();
